import { createSlice } from '@reduxjs/toolkit';
import { getEffectiveUnitPrice, getPricingScopeKey } from '../utils/quantityPricing';

// Helper: obtener clave única del item en el carrito
const getItemKey = (item) => item.cartItemId || item._id;

// Recalcula el total del carrito a partir de los items (evita drift por sumas/restas incrementales)
const recalcTotal = (state) => {
  state.total = state.items.reduce((acc, item) => acc + item.price * item.quantity, 0);
};

// Recalcula el precio unitario de cada item según la CANTIDAD TOTAL de su categoría
// en todo el carrito (no solo la cantidad de ese producto/variante puntual), para que
// el descuento por cantidad aplique aunque el cliente combine productos distintos de
// la misma categoría (ej. Canguros diferentes).
const recalcCategoryPrices = (state) => {
  const config = state.quantityPricingByScope || {};
  const qtyByScope = {};

  state.items.forEach(item => {
    const pricingKey = getPricingScopeKey(item.category, item.subcategory);
    if (!pricingKey.startsWith('::')) {
      qtyByScope[pricingKey] = (qtyByScope[pricingKey] || 0) + item.quantity;
    }
  });

  state.items.forEach(item => {
    const pricingKey = getPricingScopeKey(item.category, item.subcategory);
    const tiers = config[pricingKey];
    if (!tiers || tiers.length === 0) return;
    if (item.unitBasePrice === undefined || item.unitBasePrice === null) return;

    const totalQty = qtyByScope[pricingKey] || item.quantity;
    item.price = getEffectiveUnitPrice(tiers, totalQty, item.unitBasePrice);
  });
};

const cartSlice = createSlice({
  name: 'cart',
  initialState: { items: [], total: 0, quantityPricingByScope: {} },
  reducers: {
    addToCart: (state, action) => {
      if (!state.quantityPricingByScope) state.quantityPricingByScope = {};
      const newQuantity = action.payload.quantity || 1;
      const key = getItemKey(action.payload);
      const existingItem = state.items.find(item => getItemKey(item) === key);
      if (existingItem) {
        existingItem.quantity += newQuantity;
        if (action.payload.price !== undefined) {
          existingItem.price = action.payload.price;
        }
        if (action.payload.unitBasePrice !== undefined) {
          existingItem.unitBasePrice = action.payload.unitBasePrice;
        }
      } else {
        state.items.push({ ...action.payload, quantity: newQuantity });
      }
      recalcCategoryPrices(state);
      recalcTotal(state);
    },    
    removeFromCart: (state, action) => {
      if (!state.quantityPricingByScope) state.quantityPricingByScope = {};
      const key = getItemKey(action.payload);
      const itemIndex = state.items.findIndex(item => getItemKey(item) === key);
      if (itemIndex !== -1) {
        const item = state.items[itemIndex];
        if (item.quantity > 1) {
          item.quantity -= 1;
        } else {
          state.items.splice(itemIndex, 1);
        }
        recalcCategoryPrices(state);
        recalcTotal(state);
      }
    },
    // Actualiza la cantidad de un item existente. El precio unitario se recalcula
    // automáticamente según la cantidad total de su categoría en el carrito.
    updateItemQuantity: (state, action) => {
      if (!state.quantityPricingByScope) state.quantityPricingByScope = {};
      const { item, quantity, price } = action.payload;
      const key = getItemKey(item);
      const existingItem = state.items.find(i => getItemKey(i) === key);
      if (existingItem) {
        if (quantity <= 0) {
          state.items = state.items.filter(i => getItemKey(i) !== key);
        } else {
          existingItem.quantity = quantity;
          if (price !== undefined) existingItem.price = price;
        }
        recalcCategoryPrices(state);
        recalcTotal(state);
      }
    },
    deleteFromCart: (state, action) => {
      if (!state.quantityPricingByScope) state.quantityPricingByScope = {};
      const key = getItemKey(action.payload);
      const itemIndex = state.items.findIndex(item => getItemKey(item) === key);
      if (itemIndex !== -1) {
        state.items.splice(itemIndex, 1);
        recalcCategoryPrices(state);
        recalcTotal(state);
      }
    },
    // Guarda los tiers de precios por cantidad por categoría (cargados desde
    // QuantityPricingContext) y recalcula los precios de los items ya en el carrito.
    setQuantityPricingConfig: (state, action) => {
      state.quantityPricingByScope = action.payload || {};
      delete state.quantityPricingByCategory;
      recalcCategoryPrices(state);
      recalcTotal(state);
    },
    clearCart: (state) => {
      state.items = [];
      state.total = 0;
    }    
  }
});

export const { addToCart, removeFromCart, updateItemQuantity, deleteFromCart, clearCart, setQuantityPricingConfig } = cartSlice.actions;
export default cartSlice.reducer;
